// components/CreateOptionsSheet.js
//
// The chooser behind the centre Create button, mirroring the app's
// CreateOptionsBottomSheet: an invitation, or a greeting card.
//
// Create is the only tab that does not navigate. It asks first, because the
// two things it can make are different enough that guessing wrong costs a
// half-filled form. On the app this is a bottom sheet; here it is the same
// sheet on phones and a centred dialog on desktop, where a sheet glued to the
// bottom of a 1440px window is just a strip of buttons a long way from the
// pointer.

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { IoSparkles, IoMailOutline } from 'react-icons/io5';
import './CreateOptionsSheet.scss';

const OPTIONS = [
  {
    id: 'invitation',
    icon: IoSparkles,
    title: 'Invitation',
    body: 'A page for your event — cover, date, place, RSVPs.',
  },
  {
    id: 'card',
    icon: IoMailOutline,
    title: 'Greeting card',
    body: 'A card to send someone. Birthdays, thanks, anything.',
  },
];

export default function CreateOptionsSheet({ open, onClose, onSelectInvitation, onSelectCard }) {
  const panelRef = useRef(null);

  // Escape closes, and focus moves into the sheet so a keyboard user is not
  // left tabbing through the page behind it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.activeElement;
    panelRef.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open, onClose]);

  const choose = (id) => {
    onClose();
    if (id === 'invitation') onSelectInvitation();
    else onSelectCard();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="create-sheet-scrim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <motion.div
            ref={panelRef}
            className="create-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="What do you want to make?"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-sheet-grabber" aria-hidden="true" />

            <div className="create-sheet-head">
              <h2>What are you making?</h2>
              <button type="button" onClick={onClose} aria-label="Close">
                <FiX />
              </button>
            </div>

            <div className="create-sheet-options">
              {OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="create-sheet-option"
                  onClick={() => choose(option.id)}
                >
                  <span className="create-sheet-option-icon">
                    <option.icon />
                  </span>
                  <span className="create-sheet-option-text">
                    <strong>{option.title}</strong>
                    <small>{option.body}</small>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
